import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { capitalizeFirstLetter } from '@gitbook/react-uikit';
import { Button, type ButtonKind, type ButtonProps, LoadingIndicator } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useContentKitConfirm } from './ContentKitConfirm';
import { ContentKitIcon } from './ContentKitIcon';
import type { ContentKitElementProps } from './types';

const logger = Logger('contentkit:button');

export function ContentKitButton(
    props: ContentKitElementProps<api.ContentKitButton> & {
        buttonProps?: Partial<ButtonProps>;
        buttonDefaultKind?: Partial<ButtonKind>;
    }
): React.ReactElement | null {
    const { element, dispatchAction, buttonProps, buttonDefaultKind = 'secondary' } = props;

    const [confirm, wrapConfirm] = useContentKitConfirm();
    const [loading, wrapLoading] = useLoadingState();

    const onPress = () => {
        wrapLoading(() =>
            wrapConfirm(element.confirm, async () => {
                return dispatchAction(element.onPress);
            })
        );
    };

    /**
     * The `label` and `tooltip` props are not required, while our Spine
     * Button component requires a `label` for ux and accessibility reasons.
     *
     * We can't make those props required, that would result in a breaking change.
     * Considering that the case where the user would omit the `label` is most probably
     * for icon-only buttons, we'll fallback to using the tootltip or icons' name as
     * the label (which will be used for tooltip and aria-label on the button). And
     * if none of those are defined then we consider this button unusable, so we
     * don't render it and log an error to facilitate debugging.
     */
    if (!element.label && !element.tooltip && !element.icon && !element.trailingIcon) {
        logger.debug(
            'Cannot display button without any semantics. User should add a label, tooltip or icon',
            element
        );
        return null;
    }

    const labelFromIcon = element.icon ? capitalizeFirstLetter(element.icon) : '';
    const labelFromTrailingIcon = element.trailingIcon
        ? capitalizeFirstLetter(element.trailingIcon)
        : '';

    return (
        <>
            <Button
                label={element.label || element.tooltip || labelFromIcon || labelFromTrailingIcon}
                kind={element.style || buttonDefaultKind}
                size="medium"
                iconOnly={!element.label}
                disabled={element.disabled || loading}
                leadingIcon={
                    loading ? (
                        LoadingIndicator
                    ) : element.icon ? (
                        <ContentKitIcon icon={element.icon} size={16} />
                    ) : undefined
                }
                trailingIcon={
                    element.trailingIcon ? (
                        <ContentKitIcon icon={element.trailingIcon} size={16} />
                    ) : undefined
                }
                onPress={onPress}
                title={element.tooltip}
                {...buttonProps}
            />
            {confirm}
        </>
    );
}
